import {
  Alert,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle,
  Download,
  Eye,
  FileText,
  Filter,
  MoreVertical,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import adminApi from "../../api/admin";
import Button from "../../components/common/Button";
import { documentStatusOptions } from "../../utils/adminDropdownOptions";
import {
  formatDateForDisplay,
  getActionLabel,
  getCategoryColor,
  getDialogTitle,
  getFileIcon,
  getStatusColor,
} from "../../utils/helpers";
import { documentCategories } from "../../utils/professionalDropdownOptions";

const UserDocumentsTab = ({
  professional,
  professionalIsLoading,
  userId,
  refetchDocuments,
}) => {
  const queryClient = useQueryClient();
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [individualActionMenu, setIndividualActionMenu] = useState(null);
  const [bulkActionDialog, setBulkActionDialog] = useState(null);
  const [individualActionDialog, setIndividualActionDialog] = useState(null);
  const [currentDocument, setCurrentDocument] = useState(null);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [rejectionReason, setRejectionReason] = useState("");
  const [verificationNotes, setVerificationNotes] = useState("");
  const isBulkActionVisible = selectedDocuments.length > 0;

  // Bulk action mutation
  const bulkActionMutation = useMutation({
    mutationFn: ({ userId, payload }) =>
      adminApi.bulkVerifyDocuments(userId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["verify-professional", userId],
      });
      toast.success("Bulk action completed successfully!");
      setBulkActionDialog(null);
      setSelectedDocuments([]);
      setRejectionReason("");
      setVerificationNotes("");
      refetchDocuments();
    },
    onError: (error) => {
      console.error("Failed to perform bulk action:", error);
      toast.error(
        error.response?.data?.message || "Failed to perform bulk action"
      );
    },
  });

  // Individual document action mutation
  const individualActionMutation = useMutation({
    mutationFn: ({ userId, documentId, payload }) =>
      adminApi.verifyIndividualDocument(userId, documentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["verify-professional", userId],
      });
      toast.success("Document action completed successfully!");
      setIndividualActionDialog(null);
      setIndividualActionMenu(null);
      setCurrentDocument(null);
      setRejectionReason("");
      setVerificationNotes("");
      refetchDocuments();
    },
    onError: (error) => {
      console.error("Failed to perform document action:", error);
      toast.error(
        error.response?.data?.message || "Failed to perform document action"
      );
    },
  });

  const handleDocumentSelect = (documentId) => {
    setSelectedDocuments((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleSelectAll = () => {
    const filteredDocs = getFilteredDocuments();
    if (selectedDocuments.length === filteredDocs.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocs.map((doc) => doc.id));
    }
  };

  const handleBulkAction = (action) => {
    if (selectedDocuments.length === 0) {
      toast.warning("Please select at least one document");
      return;
    }

    if (action === "verify") {
      setBulkActionDialog("verify");
    } else if (action === "reject") {
      setBulkActionDialog("reject");
    }
    setActionMenuAnchor(null);
  };

  const handleIndividualAction = (document, action) => {
    setCurrentDocument(document);
    setIndividualActionDialog(action);
    setIndividualActionMenu(null);
  };

  const submitBulkAction = () => {
    if (!bulkActionDialog) return;

    let payload = {};

    if (bulkActionDialog === "verify") {
      payload = {
        action: "verify",
        category_filter: filterCategory !== "all" ? filterCategory : undefined,
        status_filter: filterStatus !== "all" ? filterStatus : undefined,
        notes: verificationNotes,
      };
    } else if (bulkActionDialog === "reject") {
      payload = {
        action: "reject",
        document_ids: selectedDocuments,
        rejection_reason: rejectionReason,
        notes: verificationNotes,
      };
    }

    bulkActionMutation.mutate({
      userId,
      payload,
    });
  };

  const submitIndividualAction = () => {
    if (!individualActionDialog || !currentDocument) return;

    let payload = {
      action: individualActionDialog,
      notes: verificationNotes,
    };

    if (individualActionDialog === "reject") {
      payload.rejection_reason = rejectionReason;
    }

    individualActionMutation.mutate({
      userId,
      documentId: currentDocument.id,
      payload,
    });
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "verify":
        return <CheckCircle className="w-4 h-4 mr-2 text-green-600" />;
      case "reject":
        return <XCircle className="w-4 h-4 mr-2 text-red-600" />;
      case "needs_resubmission":
        return <RefreshCw className="w-4 h-4 mr-2 text-yellow-600" />;
      default:
        return <CheckCircle className="w-4 h-4 mr-2" />;
    }
  };

  const getFilteredDocuments = () => {
    if (!professional?.documents) return [];

    return professional.documents.filter((doc) => {
      const categoryMatch =
        filterCategory === "all" || doc.category === filterCategory;
      const statusMatch =
        filterStatus === "all" || doc.verification_status === filterStatus;
      return categoryMatch && statusMatch;
    });
  };

  const filteredDocuments = getFilteredDocuments();
  const allSelected =
    selectedDocuments.length > 0 &&
    selectedDocuments.length === filteredDocuments.length;
  const someSelected =
    selectedDocuments.length > 0 &&
    selectedDocuments.length < filteredDocuments.length;

  return (
    <div className="space-y-6">
      {/* Header with Filters and Bulk Actions */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">
            Professional Documents
          </h3>
          {professional?.document_summary && (
            <div className="flex flex-wrap gap-2 sm:gap-4 text-sm mt-1">
              <span className="text-gray-600">
                Total: {professional.document_summary.total_documents}
              </span>
              <span className="text-yellow-600">
                Pending: {professional.document_summary.pending_documents}
              </span>
              <span className="text-green-600">
                Verified: {professional.document_summary.verified_documents}
              </span>
              <span className="text-red-600">
                Rejected: {professional.document_summary.rejected_documents}
              </span>
              <span className="text-blue-600">
                Needs resubmission:{" "}
                {professional.document_summary.needs_resubmission_documents}
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          {/* Filters */}
          <div
            className={`flex ${
              isBulkActionVisible ? "flex-col" : "flex-row"
            } xs:flex-row gap-2 w-full sm:w-auto`}
          >
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Category</InputLabel>
              <Select
                value={filterCategory}
                label="Category"
                onChange={(e) => setFilterCategory(e.target.value)}
                startAdornment={
                  <Filter className="w-4 h-4 text-gray-400 mr-2" />
                }
              >
                <MenuItem value="all">All Categories</MenuItem>
                {documentCategories.map((cat) => (
                  <MenuItem key={cat.key} value={cat.key}>
                    {cat.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                {documentStatusOptions.map((status) => (
                  <MenuItem key={status.key} value={status.key}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Bulk Actions */}
          {selectedDocuments.length > 0 && (
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                icon={<MoreVertical className="w-4 h-4" />}
                className="w-full sm:w-auto justify-center"
              >
                Bulk Actions ({selectedDocuments.length})
              </Button>
              <Menu
                anchorEl={actionMenuAnchor}
                open={Boolean(actionMenuAnchor)}
                onClose={() => setActionMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleBulkAction("verify")}>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                  Verify Selected
                </MenuItem>
                <MenuItem onClick={() => handleBulkAction("reject")}>
                  <XCircle className="w-4 h-4 mr-2 text-red-600" />
                  Reject Selected
                </MenuItem>
              </Menu>
            </div>
          )}
        </div>
      </div>

      {/* Selection Info */}
      {selectedDocuments.length > 0 && (
        <Alert severity="info" className="mb-4">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
            <span>{selectedDocuments.length} document(s) selected.</span>
            <Button
              variant="text"
              size="sm"
              onClick={() => setSelectedDocuments([])}
              className="sm:ml-2 -ml-2 -mt-1 sm:mt-0"
            >
              Clear Selection
            </Button>
          </div>
        </Alert>
      )}

      {professionalIsLoading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading documents...</p>
        </div>
      ) : filteredDocuments.length > 0 ? (
        <div className="space-y-4">
          {/* Select All Checkbox */}
          <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onChange={handleSelectAll}
            />
            <Typography variant="body2" className="text-gray-600">
              Select all {filteredDocuments.length} documents
            </Typography>
          </div>

          {/* Documents List */}
          {filteredDocuments.map((doc) => {
            const FileIcon = getFileIcon(doc.file_name);
            const full_url = import.meta.env.VITE_BACKEND_DOMAIN + doc.file_url;
            const isSelected = selectedDocuments.includes(doc.id);

            return (
              <Card
                key={doc.id}
                variant="outlined"
                className={`hover:bg-gray-50 transition-colors ${
                  isSelected ? "ring-2 ring-primary-500 bg-blue-50" : ""
                }`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-4">
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleDocumentSelect(doc.id)}
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center justify-between flex-1 gap-3">
                      <div className="flex items-center space-x-4 flex-1 min-w-0">
                        <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                          <FileIcon className="w-6 h-6 text-gray-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <Typography
                            variant="h6"
                            className="font-medium truncate"
                          >
                            {doc.category_display}
                          </Typography>
                          <Typography variant="body2" className="text-gray-500">
                            Uploaded: {formatDateForDisplay(doc.uploaded_at)}
                          </Typography>
                          {doc.latest_verification?.verification_notes && (
                            <Typography
                              variant="caption"
                              className="text-gray-600 mt-1 block"
                            >
                              <strong>Notes:</strong>{" "}
                              {doc.latest_verification.verification_notes}
                            </Typography>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between sm:justify-end gap-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Chip
                            label={doc.category_display}
                            color={getCategoryColor(doc.category)}
                            size="small"
                            variant="outlined"
                          />

                          <Chip
                            label={doc.verification_status_display}
                            color={getStatusColor(doc.verification_status)}
                            size="small"
                          />
                        </div>

                        <div className="flex items-center gap-1">
                          <IconButton
                            size="small"
                            onClick={() => window.open(full_url, "_blank")}
                            title="Preview Document"
                            className="text-blue-600 hover:bg-blue-50"
                          >
                            <Eye className="w-4 h-4" />
                          </IconButton>

                          <IconButton
                            size="small"
                            onClick={() => {
                              const link = document.createElement("a");
                              link.href = doc.file_url;
                              link.download = doc.file_name;
                              link.click();
                            }}
                            title="Download Document"
                            className="text-green-600 hover:bg-green-50"
                          >
                            <Download className="w-4 h-4" />
                          </IconButton>

                          {/* Individual Document Actions Menu */}
                          <div className="relative">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setCurrentDocument(doc);
                                setIndividualActionMenu(e.currentTarget);
                              }}
                              title="Document Actions"
                              className="text-gray-600 hover:bg-gray-50"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </IconButton>
                            <Menu
                              anchorEl={individualActionMenu}
                              open={Boolean(
                                individualActionMenu &&
                                  currentDocument?.id === doc.id
                              )}
                              onClose={() => setIndividualActionMenu(null)}
                            >
                              <MenuItem
                                onClick={() =>
                                  handleIndividualAction(doc, "verify")
                                }
                              >
                                {getActionIcon("verify")}
                                Verify Document
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  handleIndividualAction(doc, "reject")
                                }
                              >
                                {getActionIcon("reject")}
                                Reject Document
                              </MenuItem>
                              <MenuItem
                                onClick={() =>
                                  handleIndividualAction(
                                    doc,
                                    "needs_resubmission"
                                  )
                                }
                              >
                                {getActionIcon("needs_resubmission")}
                                Request Resubmission
                              </MenuItem>
                            </Menu>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verification History */}
                  {doc.verification_history &&
                    doc.verification_history.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <Typography
                          variant="caption"
                          className="font-medium text-gray-700"
                        >
                          Verification History:
                        </Typography>
                        <div className="mt-1 space-y-1">
                          {doc.verification_history.map((history, index) => (
                            <div
                              key={history.id}
                              className="flex flex-wrap items-center gap-2 text-xs text-gray-600"
                            >
                              <span>{history.status_display}</span>
                              {history.verified_by && (
                                <span>
                                  • Verified by: {history.verified_by}
                                </span>
                              )}
                              {history.verification_date && (
                                <span>
                                  •{" "}
                                  {formatDateForDisplay(
                                    history.verification_date
                                  )}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-secondary-500">
          <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>No documents found matching your filters</p>
        </div>
      )}

      {/* Bulk Action Dialogs */}
      <Dialog
        open={Boolean(bulkActionDialog)}
        onClose={() => setBulkActionDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkActionDialog === "verify"
            ? "Bulk Verify Documents"
            : "Bulk Reject Documents"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} className="mt-2">
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                {bulkActionDialog === "verify"
                  ? `You are about to verify ${selectedDocuments.length} document(s)`
                  : `You are about to reject ${selectedDocuments.length} document(s)`}
              </Alert>
            </Grid>

            {bulkActionDialog === "reject" && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required
                  multiline
                  rows={3}
                  placeholder="Please provide a reason for rejection..."
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Verification Notes"
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any additional notes about this verification..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline"
            onClick={() => setBulkActionDialog(null)}
            disabled={bulkActionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submitBulkAction}
            loading={bulkActionMutation.isPending}
            variant={bulkActionDialog === "reject" ? "danger" : "primary"}
          >
            {bulkActionMutation.isPending
              ? "Processing..."
              : bulkActionDialog === "verify"
              ? "Verify Documents"
              : "Reject Documents"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Action Dialog */}
      <Dialog
        open={Boolean(individualActionDialog)}
        onClose={() => setIndividualActionDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getDialogTitle(individualActionDialog)}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} className="mt-2">
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                {currentDocument &&
                  `You are about to ${getActionLabel(
                    individualActionDialog
                  )?.toLowerCase()} the document: "${
                    currentDocument.category_display
                  }"`}
              </Alert>
            </Grid>

            {(individualActionDialog === "reject" ||
              individualActionDialog === "needs_resubmission") && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={
                    individualActionDialog === "reject"
                      ? "Rejection Reason"
                      : "Resubmission Reason"
                  }
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  required={individualActionDialog === "reject"}
                  multiline
                  rows={3}
                  placeholder={
                    individualActionDialog === "reject"
                      ? "Please provide a reason for rejection..."
                      : "Please specify what needs to be resubmitted..."
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Verification Notes"
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                multiline
                rows={3}
                placeholder="Add any additional notes about this action..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outline"
            onClick={() => setIndividualActionDialog(null)}
            disabled={individualActionMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={submitIndividualAction}
            loading={individualActionMutation.isPending}
            variant={
              individualActionDialog === "reject"
                ? "danger"
                : individualActionDialog === "needs_resubmission"
                ? "warning"
                : "primary"
            }
          >
            {individualActionMutation.isPending
              ? "Processing..."
              : getActionLabel(individualActionDialog)}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default UserDocumentsTab;
